import type { FormSchemaResolved } from '#layers/block-engine/public';
import * as z from 'zod/v4-mini';
import { AuthProtocol, defineAuthProvider } from '#layers/access/types/auth-providers/define';
import { oauth2, oauth2ConfigCreateSchema, oauth2ConfigSchema } from '#layers/access/utils/auth-providers/protocols/oauth2';
import { nonEmptyString } from '#layers/base/public';
import { applyOAuth2PresetOptions, oauth2PresetDefaults, OIDC_CLAIM_MAPPINGS, OIDC_SCOPES } from './oauth2-preset';

// `<digits>-<chars>.apps.googleusercontent.com` — Google's canonical Client ID
// shape. Catches typos / placeholders / wrong pastes before the user discovers
// the failure at sign-in via Google's opaque `invalid_client` error.
const GOOGLE_CLIENT_ID = /^\d+-[\w-]+\.apps\.googleusercontent\.com$/;

const googleClientIdSchema = nonEmptyString().check(
	z.regex(GOOGLE_CLIENT_ID, '$t:sso_providers.google.errors.invalid_client_id_format'),
);

const googleConfigSchema = z.object({
	...oauth2ConfigSchema.def.shape,
	clientId: googleClientIdSchema,
});

const googleConfigCreateSchema = z.object({
	...oauth2ConfigCreateSchema.def.shape,
	clientId: googleClientIdSchema,
});

const GOOGLE_WIRE_DEFAULTS = {
	authorizationEndpoint: 'https://accounts.google.com/o/oauth2/v2/auth',
	tokenEndpoint: 'https://oauth2.googleapis.com/token',
	userinfoEndpoint: 'https://openidconnect.googleapis.com/v1/userinfo',
	scopes: OIDC_SCOPES,
	claimMappings: OIDC_CLAIM_MAPPINGS,
} as const;

const GOOGLE_DEFAULTS = oauth2PresetDefaults(GOOGLE_WIRE_DEFAULTS);

const GOOGLE_AUTH_CLIENTS_URL = 'https://console.cloud.google.com/auth/clients';

const GOOGLE_DESCRIPTIONS: Record<string, string> = {
	clientId: '$t:sso_providers.google.client_id_hint',
	clientSecret: '$t:sso_providers.google.client_secret_hint',
};

/**
 * Walk the inherited oauth2 form schema, apply Google's prefilled defaults
 * and Google-specific field descriptions, and force the wrapping group's
 * `defaultOpen` to false (so the prefilled section is collapsed by default).
 */
function applyGoogleDefaults(items: FormSchemaResolved<any>): FormSchemaResolved<any> {
	return applyOAuth2PresetOptions(items, {
		defaults: GOOGLE_DEFAULTS,
		descriptions: GOOGLE_DESCRIPTIONS,
	});
}

export const google = defineAuthProvider({
	id: 'google',
	name: 'Google',
	description: '$t:sso_providers.google.description',
	protocol: AuthProtocol.OAuth2,
	icon: 'logos:google-icon',
	docsUrl: 'https://developers.google.com/identity/protocols/oauth2/web-server',
	walkthroughStepKeys: [
		{
			key: 'sso_providers.google.walkthrough.console',
			href: GOOGLE_AUTH_CLIENTS_URL,
		},
		{
			key: 'sso_providers.google.walkthrough.create_client',
			href: GOOGLE_AUTH_CLIENTS_URL,
		},
		'sso_providers.google.walkthrough.redirect',
		'sso_providers.google.walkthrough.credentials',
	],
	extends: oauth2,
	schema: googleConfigCreateSchema,
	editSchema: googleConfigSchema,
	options: ({ form, context }) =>
		form(applyGoogleDefaults(context.base?.resolvedOptions ?? []) as never),
});
